import type { DocAttachment, DocDetail } from "./types.js";

/**
 * What the article reader (`ArticlePage`) renders for a doc.
 *
 * The reader is **type-aware** — it never dumps a raw markdown body / web
 * scrape as plain text. The policy (reported 2026-06-09, "we must show ALL
 * documentations using the PDF reader … we should not show anything in plain
 * text", plus FAQ-as-Q&A and one-video-per-doc):
 *
 *   1. A playable VIDEO payload   → the video player (`VideoReader`).
 *   2. A parseable FAQ            → the Q&A viewer (`FaqReader`), one
 *                                   question + answer per item — never the
 *                                   canonical `## Q / A` markdown as text.
 *   3. A faithful ORIGINAL PDF    → the PDF reader, fed by the `source_file`
 *      (`source_file`)              passthrough. This wins over the
 *                                   transcribed markdown body, so an imported
 *                                   marketing/whitepaper page shows its real
 *                                   PDF, not the scraped page text (THE
 *                                   "we're showing the website itself" bug).
 *   4. An uploaded PDF            → the PDF reader, fed by the `/file`
 *                                   passthrough.
 *   5. An uploaded image / A-V    → the native `<img>` / `<video>` / `<audio>`
 *                                   (a picture or clip is not "plain text",
 *                                   and wrapping it in a generated PDF would be
 *                                   lossy) — see `FilePreview`.
 *   6. Any other uploaded binary  → a download card (Office / zip / … can't be
 *                                   previewed in the browser).
 *   7. A markdown BODY, no file   → the Markdown renderer.
 *   8. Nothing at all             → an empty-state note.
 *
 * RULE 7 IS THIS PORT'S ONE DIVERGENCE from the command center. There, an
 * authored body is rendered as a PDF **built client-side** (`buildDocPdf`)
 * and shown on the pdf.js canvas, so every document looks like a document.
 * Here it is rendered as markdown instead.
 *
 * That divergence was originally FORCED: this portal shipped a stubbed
 * `<PdfReader>`, so the PDF branch could only ever show a Download card, and
 * routing an article through it would have replaced a readable page with
 * "this PDF can’t be previewed inline". That constraint is gone — the CRM's
 * invoice and quote pages needed a real inline reader, so pdf.js is now here
 * (see `components/pdf/pdf-reader.tsx`).
 *
 * The rule is KEPT anyway, now as a choice rather than a limitation: an
 * authored body is web content, and `<Markdown>` gives it real headings,
 * lists, tables and selectable, searchable, reflowing text. Rendering it as a
 * paginated A4 sheet is a worse reading experience than the source's own
 * users get for the same content elsewhere. Changing this is a product
 * decision, not a technical one — nothing blocks it any more.
 *
 * Rules 3 and 4 (an actual PDF file) render inline on the pdf.js canvas, the
 * same as the command center. `buildDocPdf` also backs the reader's Download
 * button.
 *
 * This selector is PURE (no React, no I/O) so the precedence is unit-tested in
 * `docReader.test.ts`; `ArticlePage` maps each mode to a renderer + (for the
 * PDF modes) the byte loader. Mirrors the customer portal's `Article.tsx`
 * dispatch (video → faq → fallback) so the two readers agree on which medium a
 * doc is.
 */
export type DocReaderMode =
  | { kind: "video" }
  | { kind: "faq" }
  /** Render through the inline PDF reader. `via` picks the byte source:
   * `source` = the `source_file` original PDF, `file` = the uploaded PDF
   * attachment. The command center has a third source, `built` (a PDF
   * generated from the markdown body); this port does not, because rule 7
   * renders that body as markdown instead — see the note above. */
  | { kind: "pdf"; via: "source" | "file"; file: DocAttachment | null }
  | { kind: "image"; file: DocAttachment }
  | { kind: "av"; media: "video" | "audio"; file: DocAttachment }
  /** A hand-authored markdown BODY rendered inline. Distinct from `text`,
   * which is an uploaded FILE whose bytes have to be fetched first. */
  | { kind: "markdown" }
  /** An uploaded TEXT file rendered inline — `.md`/`.markdown` through the
   * Markdown renderer (`markdown: true`), any other plain-text file (`.txt`,
   * `.csv`, `.json`, `.log`, …) as preformatted text. Bytes come through the
   * same-origin `/file` passthrough, so it's desktop-safe like the PDF modes. */
  | { kind: "text"; file: DocAttachment; markdown: boolean }
  | { kind: "file-card"; file: DocAttachment }
  | { kind: "empty" };

/** Lowercased file extension (no dot), or "" when there isn't one. */
function ext(filename: string): string {
  const m = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] ?? "";
}

/** True when an attachment is a PDF (by MIME or `.pdf` extension). */
export function attachmentIsPdf(a: Pick<DocAttachment, "content_type" | "filename">): boolean {
  const ct = (a.content_type ?? "").toLowerCase();
  return ct === "application/pdf" || a.filename.toLowerCase().endsWith(".pdf");
}

const MARKDOWN_EXTS = ["md", "markdown", "mdown", "mkd", "mkdn"];
const TEXT_EXTS = [...MARKDOWN_EXTS, "txt", "text", "csv", "tsv", "log", "json", "yml", "yaml", "xml"];

/** True when an attachment is MARKDOWN (render through the Markdown renderer). */
export function attachmentIsMarkdown(a: Pick<DocAttachment, "content_type" | "filename">): boolean {
  const ct = (a.content_type ?? "").toLowerCase();
  return ct === "text/markdown" || ct === "text/x-markdown" || MARKDOWN_EXTS.includes(ext(a.filename));
}

/** True when an attachment is any plain-TEXT file the browser can decode +
 * render inline (markdown, txt, csv, json, …). Kept in step with the
 * `classifyFile` "text" class in `docPdf.ts` (the Download-PDF builder). */
export function attachmentIsText(a: Pick<DocAttachment, "content_type" | "filename">): boolean {
  const ct = (a.content_type ?? "").toLowerCase();
  return (
    ct.startsWith("text/") ||
    ct === "application/json" ||
    ct === "application/xml" ||
    TEXT_EXTS.includes(ext(a.filename))
  );
}

function hasBody(detail: Pick<DocDetail, "content">): boolean {
  return !!detail.content && detail.content.trim().length > 0;
}

export function selectDocReaderMode(detail: DocDetail): DocReaderMode {
  // 1. Playable video — route on the payload's presence (not doc_type), so a
  //    legacy article-typed doc whose video lives in `metadata.video_url`
  //    still plays. The serializer only emits `video` when it's playable.
  if (detail.video) return { kind: "video" };

  // 2. Parseable FAQ.
  if (detail.doc_type === "faq" && (detail.faq_items?.length ?? 0) > 0) {
    return { kind: "faq" };
  }

  // 3. Faithful original PDF wins over the transcribed body.
  const source = detail.source_file ?? null;
  if (source && attachmentIsPdf(source)) {
    return { kind: "pdf", via: "source", file: source };
  }

  // 4–6. Uploaded file (upload-mode docs have an empty body, so this and the
  //      body branch below are mutually exclusive in practice).
  const file = detail.files[0] ?? null;
  if (file) {
    if (attachmentIsPdf(file)) return { kind: "pdf", via: "file", file };
    const ct = (file.content_type ?? "").toLowerCase();
    if (ct.startsWith("image/")) return { kind: "image", file };
    if (ct.startsWith("video/")) return { kind: "av", media: "video", file };
    if (ct.startsWith("audio/")) return { kind: "av", media: "audio", file };
    // A text/markdown upload renders inline (Markdown for `.md`, preformatted
    // for other text) instead of falling through to the download-only card.
    if (attachmentIsText(file)) return { kind: "text", file, markdown: attachmentIsMarkdown(file) };
    return { kind: "file-card", file };
  }

  // 7. Markdown body, no file → render the markdown. See the note above for
  //    why this differs from the command center's built-PDF branch.
  if (hasBody(detail)) return { kind: "markdown" };

  // 8. Nothing.
  return { kind: "empty" };
}
